import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { CALL_OUTCOMES } from "@/lib/statuses";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Printer, X } from "lucide-react";
import { printCallSheet } from "@/lib/print";

const USER_KEY = "studio_current_user";

// Fenêtres autorisées côté client (miroir de backend/availability.py, servies par l'API)
function slotError(startStr, rules) {
  if (!startStr || !rules) return "";
  const d = new Date(startStr);
  if (Number.isNaN(d.getTime())) return "";
  const wd = (d.getDay() + 6) % 7; // 0 = lundi
  const toMin = (hhmm) => { const [h, m] = hhmm.split(":"); return +h * 60 + +m; };
  const day = rules.days?.[String(wd)] || {};
  const min = day.min && toMin(day.min) > toMin(rules.global_min) ? day.min : rules.global_min;
  const minutes = d.getHours() * 60 + d.getMinutes();
  const label = rules.day_labels?.[wd] || "";
  if (minutes < toMin(min)) return `Le ${label}, le premier RDV possible est à ${min.replace(":", "h")}.`;
  if (day.max && minutes > toMin(day.max)) return `Le ${label}, le dernier RDV possible est à ${day.max.replace(":", "h")}.`;
  return "";
}

function defaultStart(offsetHours = 24) {
  const d = new Date(Date.now() + offsetHours * 3600 * 1000);
  d.setMinutes(Math.round(d.getMinutes() / 15) * 15, 0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label className="eyebrow">{label}</Label>
    {children}
  </div>
);

export const CallReportModal = ({ prospect, open, onOpenChange, onSaved }) => {
  const [mode, setMode] = useState(null); // null | 'rdv' | 'rappel'
  const [users, setUsers] = useState([]);
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const currentUser = useMemo(() => localStorage.getItem(USER_KEY) || "", []);

  useEffect(() => {
    axios.get(`${API}/users`).then((r) => setUsers(r.data.users || [])).catch(() => {});
    axios.get(`${API}/availability/rules`).then((r) => setRules(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open || !prospect) return;
    setMode(null);
    setForm({
      start: defaultStart(24),
      title: `RDV — ${prospect.nom || ""}`,
      location: "",
      interlocuteur: "",
      interlocuteur_role: "",
      budget: "",
      besoin: "",
      urgence: "",
      objections: "",
      note: "",
      motif: "",
      assigned_to: currentUser || "",
    });
  }, [open, prospect, currentUser]);

  if (!prospect) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const rdvSlotError = mode === "rdv" ? slotError(form.start, rules) : "";

  const snapToFirstSlot = () => {
    if (!rules || !form.start) return;
    const d = new Date(form.start);
    const wd = (d.getDay() + 6) % 7;
    const day = rules.days?.[String(wd)] || {};
    const toMin = (hhmm) => { const [h, m] = hhmm.split(":"); return +h * 60 + +m; };
    const min = day.min && toMin(day.min) > toMin(rules.global_min) ? day.min : rules.global_min;
    const minutes = d.getHours() * 60 + d.getMinutes();
    const target = day.max && minutes > toMin(day.max) ? day.max : min;
    const [h, m] = target.split(":");
    d.setHours(+h, +m, 0, 0);
    setForm((f) => ({ ...f, start: new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16) }));
  };

  const quickOutcome = async (status) => {
    setSaving(true);
    try {
      await axios.post(`${API}/prospects/${prospect.siret}/call-report`, {
        status, note: form.note || "", created_by: form.assigned_to || currentUser,
      });
      toast.success("Compte rendu enregistré");
      onSaved?.({ siret: prospect.siret, status });
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const saveEvent = async (type) => {
    if (!form.start) { toast.error("Renseignez la date et l'heure"); return; }
    if (type === "rdv") {
      const err = slotError(form.start, rules);
      if (err) { toast.error(err); return; }
    }
    setSaving(true);
    try {
      if (form.assigned_to) localStorage.setItem(USER_KEY, form.assigned_to);
      const r = await axios.post(`${API}/events`, {
        type,
        prospect_siret: prospect.siret,
        start: form.start,
        duration_minutes: type === "rdv" ? 45 : 15,
        title: type === "rdv" ? form.title : `Rappel — ${prospect.nom || ""}`,
        location: form.location,
        interlocuteur: form.interlocuteur,
        interlocuteur_role: form.interlocuteur_role,
        budget: form.budget,
        besoin: form.besoin,
        urgence: form.urgence,
        objections: form.objections,
        note: form.note,
        motif: form.motif,
        assigned_to: form.assigned_to,
        created_by: form.assigned_to || currentUser,
      });
      const ev = r.data.event;
      if (type === "rdv") {
        toast.success(ev.google_sync_status === "synchronise"
          ? "RDV enregistré et synchronisé avec Google Calendar"
          : "RDV enregistré — synchronisation Google en attente");
      } else {
        toast.success("Rappel enregistré dans le calendrier Studio");
      }
      onSaved?.({ siret: prospect.siret, status: type === "rdv" ? "rdv_pris" : "rappel", event: ev });
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl w-[calc(100vw-2rem)] max-h-[92vh] overflow-y-auto bg-white border-brand-line rounded-md p-6 sm:p-8"
        data-testid="call-report-modal"
      >
        <DialogHeader className="text-left space-y-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="eyebrow">Compte rendu call center</div>
              <DialogTitle className="font-display text-2xl sm:text-3xl text-brand-ink mt-2 truncate">
                {prospect.nom}
              </DialogTitle>
              <div className="font-mono text-xs text-brand-subtle mt-2">
                {prospect.google_phone || "Aucun téléphone"} {prospect.ville ? `· ${prospect.ville}` : ""}
              </div>
              <DialogDescription className="sr-only">
                Compte rendu d'appel pour {prospect.nom} : choisissez une issue d'appel, puis complétez le RDV ou le rappel.
              </DialogDescription>
            </div>
            <div className="flex flex-col gap-2">
              <Button variant="outline" size="sm" onClick={() => printCallSheet(prospect)}
                className="border-brand-line text-brand-ink hover:bg-brand-paper rounded-md" data-testid="call-print-sheet">
                <Printer className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Fiche
              </Button>
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}
                className="border-brand-line text-brand-subtle hover:bg-brand-paper rounded-md" data-testid="call-close">
                <X className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Fermer
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Issues d'appel */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-7">
          {CALL_OUTCOMES.map((o) => {
            const active = (o.opens && mode === o.opens);
            return (
              <button
                key={o.status}
                disabled={saving}
                data-testid={`call-outcome-${o.status}`}
                onClick={() => (o.opens ? setMode(mode === o.opens ? null : o.opens) : quickOutcome(o.status))}
                className={`h-11 rounded-md border text-[11px] font-mono uppercase tracking-[0.16em] transition-colors ${
                  active || (o.primary && !mode)
                    ? o.primary && !active
                      ? "bg-brand-ink text-white border-brand-ink hover:bg-brand-moss"
                      : "bg-brand-moss text-white border-brand-moss"
                    : "bg-white text-brand-ink border-brand-line hover:border-brand-ink/25 hover:bg-brand-paper"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>

        {mode === "rdv" && (
          <div className="mt-8 pt-7 border-t border-brand-line grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="rdv-form">
            <Field label="Date du RDV">
              <Input type="datetime-local" value={form.start} onChange={set("start")}
                className={`mt-1 h-10 font-mono text-sm rounded-md ${rdvSlotError ? "border-[#D8A695] focus-visible:ring-[#D8A695]/30" : "border-brand-line"}`}
                data-testid="rdv-start" />
              {rdvSlotError ? (
                <div className="mt-2" data-testid="rdv-slot-error">
                  <p className="text-[11px] text-[#8A3F27]">{rdvSlotError}</p>
                  <button type="button" onClick={snapToFirstSlot} data-testid="rdv-snap-slot"
                    className="mt-1.5 text-[10px] font-mono uppercase tracking-[0.14em] text-brand-moss hover:underline">
                    Utiliser le premier créneau valide
                  </button>
                </div>
              ) : (
                rules?.human && (
                  <p className="mt-2 text-[11px] text-brand-subtle leading-relaxed" data-testid="rdv-slot-rules">
                    {rules.human.join(" · ")}
                  </p>
                )
              )}
            </Field>
            <Field label="Lieu / lien">
              <Input placeholder="Téléphone, Google Meet, adresse…" value={form.location} onChange={set("location")}
                className="mt-1 h-10 text-sm border-brand-line rounded-md" data-testid="rdv-location" />
            </Field>
            <Field label="Titre calendrier" full>
              <Input value={form.title} onChange={set("title")}
                className="mt-1 h-10 text-sm border-brand-line rounded-md" data-testid="rdv-title" />
            </Field>
            <Field label="Interlocuteur">
              <Input placeholder="Nom, rôle, décideur…" value={form.interlocuteur} onChange={set("interlocuteur")}
                className="mt-1 h-10 text-sm border-brand-line rounded-md" data-testid="rdv-interlocuteur" />
            </Field>
            <Field label="Budget estimé">
              <Input placeholder="Ex. 2 000–5 000 €, à confirmer…" value={form.budget} onChange={set("budget")}
                className="mt-1 h-10 text-sm border-brand-line rounded-md" data-testid="rdv-budget" />
            </Field>
            <Field label="Besoin détecté">
              <Input placeholder="Site vitrine, refonte, automatisation…" value={form.besoin} onChange={set("besoin")}
                className="mt-1 h-10 text-sm border-brand-line rounded-md" data-testid="rdv-besoin" />
            </Field>
            <Field label="Urgence">
              <Input placeholder="Cette semaine, ce mois-ci, pas urgent…" value={form.urgence} onChange={set("urgence")}
                className="mt-1 h-10 text-sm border-brand-line rounded-md" data-testid="rdv-urgence" />
            </Field>
            <Field label="Objections / points à préparer" full>
              <Input placeholder="Prix, délai, prestataire actuel, besoin de voir des exemples…" value={form.objections} onChange={set("objections")}
                className="mt-1 h-10 text-sm border-brand-line rounded-md" data-testid="rdv-objections" />
            </Field>
            <Field label="Note rapide" full>
              <Textarea value={form.note} onChange={set("note")} placeholder="Contexte, ton de l'échange, prochaine étape…"
                className="mt-1 min-h-[80px] border-brand-line rounded-md" data-testid="rdv-note" />
            </Field>
            <Field label="Assigné à">
              <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm((f) => ({ ...f, assigned_to: v === "none" ? "" : v }))}>
                <SelectTrigger className="mt-1 h-10 border-brand-line rounded-md" data-testid="rdv-assigned"><SelectValue placeholder="Personne" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non assigné</SelectItem>
                  {users.map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <div className="sm:col-span-2 pt-2">
              <Button onClick={() => saveEvent("rdv")} disabled={saving || !!rdvSlotError} data-testid="rdv-save"
                className="h-11 w-full sm:w-auto px-8 bg-brand-moss hover:bg-brand-moss2 text-white rounded-md text-[11px] font-mono uppercase tracking-[0.16em] disabled:opacity-40">
                Enregistrer le RDV
              </Button>
            </div>
          </div>
        )}

        {mode === "rappel" && (
          <div className="mt-8 pt-7 border-t border-brand-line grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="rappel-form">
            <Field label="Date et heure du rappel">
              <Input type="datetime-local" value={form.start} onChange={set("start")}
                className="mt-1 h-10 font-mono text-sm border-brand-line rounded-md" data-testid="rappel-start" />
            </Field>
            <Field label="Prospect">
              <Input value={prospect.nom || ""} readOnly
                className="mt-1 h-10 text-sm border-brand-line rounded-md bg-brand-paper text-brand-subtle" />
            </Field>
            <Field label="Personne à rappeler">
              <Input placeholder="Nom, rôle…" value={form.interlocuteur} onChange={set("interlocuteur")}
                className="mt-1 h-10 text-sm border-brand-line rounded-md" data-testid="rappel-person" />
            </Field>
            <Field label="Motif">
              <Input placeholder="Absent, à rappeler après 18h, devis à envoyer…" value={form.motif} onChange={set("motif")}
                className="mt-1 h-10 text-sm border-brand-line rounded-md" data-testid="rappel-motif" />
            </Field>
            <Field label="Note" full>
              <Textarea value={form.note} onChange={set("note")} className="mt-1 min-h-[70px] border-brand-line rounded-md" data-testid="rappel-note" />
            </Field>
            <Field label="Assigné à">
              <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm((f) => ({ ...f, assigned_to: v === "none" ? "" : v }))}>
                <SelectTrigger className="mt-1 h-10 border-brand-line rounded-md" data-testid="rappel-assigned"><SelectValue placeholder="Personne" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non assigné</SelectItem>
                  {users.map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <div className="sm:col-span-2 pt-2">
              <Button onClick={() => saveEvent("rappel")} disabled={saving} data-testid="rappel-save"
                className="h-11 w-full sm:w-auto px-8 bg-brand-moss hover:bg-brand-moss2 text-white rounded-md text-[11px] font-mono uppercase tracking-[0.16em]">
                Enregistrer le rappel
              </Button>
              <p className="mt-3 text-[11px] text-brand-subtle">Les rappels restent dans le calendrier Studio et ne sont pas envoyés vers Google Calendar.</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
